/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { Button, Container, CustomBox, ExportTableButton } from '@/components';
import { useTranslation } from 'react-i18next';
import { DocumentoSeleccionado, Value } from '@/types/getPendingPaymentsType';
import { TableFilters } from '@/types/AccountStatementFilters';
import { useAuthContext, useToastNotification } from '@/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollar,
  faEdit,
  faFilter,
  faClipboardCheck,
  faBoxesStacked,
} from '@fortawesome/free-solid-svg-icons';
import { TableData } from '@/components/Table/types';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { getAccountStatement } from '@/services/getAccountStatement';
import { getPendingPayments } from '@/services/getPendingPayments';
import { deleteSettlementDocuments } from '@/services/deleteSettlementDocuments';
import { addDocumentOnSettlement } from '@/services/addDocumentOnSettlement';
import styles from './Components/AccountStamentFilter.module.scss';
import AccountStatementFilter from './Components/AccountStatementFilter';
import AccountStamentTable from './Components/AccountStamentTable';
import EditAccountStamentModal from './Components/EditAccountStamentModal';
import CreateSelectionModal from './Components/CreateSelectionModal';
import ModalPendingPayments from '../PendingPayments/ModalPendingPayments';
import PendingWithholdingsModal from './Components/PendingWithholdingsModal';
import { saveSelection } from './services/saveSelection';
import { getDataTitularByCode } from './services/getDataTitularByCode';

const today = new Date();

const AccountStatement = () => {
  const { t } = useTranslation('accountStatement');
  const [selectedItems, setSelectedItems] = useState<DocumentoSeleccionado[]>(
    []
  );
  const { showToast } = useToastNotification();

  const [sumTotal, setSumTotal] = useState<number>(0);
  const [selectionModalIsOpen, setSelectionModalIsOpen] = useState(false);
  const [withholdinsModalIsOpen, setWithholdinsModalIsOpen] = useState(false);
  const [editAccountStatementModal, setEditAccountStatementModal] =
    useState(false);
  const { userDataDecoded } = useAuthContext();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { estado_cuenta_titular, agente_retencion, name } = userDataDecoded;
  const [isPaymentDialogShown, setIsPaymentDialogShown] = useState(false);
  const [paying, setPaying] = useState(false);

  const [paymentData, setPaymentData] = useState<Value | undefined>();

  const isTitular = agente_retencion === '1';

  const initialFilters: TableFilters = {
    retencionTitular: isTitular,
    vencimiento: today,
    codigo: estado_cuenta_titular.split(';')[0],
    name,
    selection: undefined,
  };

  const [filters, setFilters] = useState(initialFilters);

  const { retencionTitular, vencimiento, codigo, selection } = filters;

  const codes = useMemo(
    () => estado_cuenta_titular.split(';'),
    [estado_cuenta_titular]
  );

  const resultsNames = useQueries({
    queries: codes?.map((cod) => {
      return {
        queryKey: ['getDataTitularByCode', cod],
        queryFn: () => getDataTitularByCode({ codigo: cod }),
        enabled: !!codigo,
      };
    }),
  });

  const titularData = useMemo(() => {
    return resultsNames?.map((result, index) => {
      const { data } = result;
      return {
        codigo: data?.codigo || codes[index],
        nombre: data?.nombre || '',
      };
    });
  }, [resultsNames, codes]);

  const codigoOptions = titularData.map(({ codigo: codigoValue, nombre }) => ({
    name: nombre,
    value: String(codigoValue),
  }));

  const {
    mutate: getData,
    isPending: isLoading,
    data: accountStatement,
  } = useMutation({
    mutationFn: getAccountStatement,
  });

  const {
    data: selectionItems,
    refetch: refetchSelection,
    isLoading: gettingSelection,
  } = useQuery({
    queryFn: () => getPendingPayments({ status: '0' }),
    queryKey: ['accountStatementSelections'],
  });

  const { data: titular } = useQuery({
    queryFn: () => getDataTitularByCode({ codigo }),
    queryKey: ['getDataTitularByCode', { codigo }],
    enabled: !!codigo,
  });

  const {
    mutate: createSelection,
    isPending: creatingSelection,
    data: selectionCreated,
    reset: resetSelectionCreated,
  } = useMutation({
    mutationFn: saveSelection,
    onSuccess: async (value) => {
      setPaying(true);
      const batchSize = 20;
      await deleteSettlementDocuments(value);
      for (let i = 0; i < selectedItems.length; i += batchSize) {
        const documentBatch = selectedItems.slice(i, i + batchSize);
        const promises = documentBatch.map((doc) =>
          addDocumentOnSettlement(doc.oidDocumento)
        );
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(promises);
      }

      refetchSelection();
      setPaying(false);
      setIsPaymentDialogShown(true);
      getData({ retencionTitular, vencimiento, codigo });
    },
    onError: (error) => {
      setPaying(false);
      const items = (error?.cause as string)?.split(',') || [1];
      items.forEach((item) => {
        showToast({
          severity: 'error',
          detail: t(error?.message, { nroDoca: item }),
        });
      });
    },
  });

  useEffect(() => {
    const newSelection = selectionItems?.find(
      ({ oid }) => selectionCreated === oid
    );
    if (!newSelection) return;
    setFilters((prev) => ({
      ...prev,
      selection: newSelection.numeroSeleccion,
    }));
    resetSelectionCreated();
  }, [selectionCreated, resetSelectionCreated, selectionItems]);

  const toggleIsPaymentDialog = () => {
    setIsPaymentDialogShown(!isPaymentDialogShown);
  };

  const onPaymentButtonClick = () => {
    if (paymentData) {
      setIsPaymentDialogShown(true);
      return;
    }

    if (!titular) return;

    const referencia = new Date().toLocaleString('es-ES').replaceAll(',', '');
    const { razonSocial, ruc: rut } = titular;
    const body = {
      documents: selectedItems,
      rut,
      razonSocial,
      referencia,
      codCliente: Number(codigo),
      vencimiento: vencimiento as Date,
      retencionTitular,
    };

    createSelection(body);
  };

  const handleSelectionChange = (e: { value: unknown }) => {
    setSelectedItems(e?.value as DocumentoSeleccionado[]);
    setSumTotal(0);
    const arrayItems = e?.value as DocumentoSeleccionado[];
    if (arrayItems.length) {
      arrayItems.forEach((item: DocumentoSeleccionado) => {
        setSumTotal((prev) => prev + item.impMovMo);
      });
    }
  };

  const dataMatched = useMemo(() => {
    const newData = (accountStatement || []).map(
      (item) => {
        const { fecDoc, fecVenc, ...rest } = item;
        const formattedFecDoc = new Date(fecDoc);
        const formatFecVenc = new Date(fecVenc);

        const body = {
          ...rest,
          fecDoc: formattedFecDoc,
          fecVenc: formatFecVenc,
        } as DocumentoSeleccionado;

        const properties = ['codDoca', 'serieDoca', 'nroDoca'];
        if (!selectionItems) return body;

        // eslint-disable-next-line no-restricted-syntax
        for (const currentSelection of selectionItems) {
          const {
            observaciones,
            numeroSeleccion,
            listaDocumentosSeleccionados,
          } = currentSelection;

          const exist = listaDocumentosSeleccionados?.find((doc) => {
            return properties.every((prop) => {
              const propString = prop as keyof DocumentoSeleccionado;
              return doc[propString] === body[propString];
            });
          });

          // eslint-disable-next-line no-continue
          if (!exist) continue;

          body.referencia = observaciones;
          body.seleccion = numeroSeleccion;
          return body;
        }

        return body;
      },
      [selectionItems]
    );

    return newData;
  }, [accountStatement, selectionItems]);

  useEffect(() => {
    const body = !selection
      ? []
      : dataMatched.filter((item) => item.seleccion === selection);
    setSelectedItems(body);
  }, [selection, dataMatched]);

  useEffect(() => {
    setSelectedItems([]);
    const body = { retencionTitular, vencimiento, codigo };
    getData(body);
  }, [retencionTitular, vencimiento, codigo, getData]);

  const filterdData = useMemo(() => {
    if (!selection) return dataMatched;
    const body = (dataMatched || []).filter(
      (item) => item.seleccion === selection
    );
    return body;
  }, [dataMatched, selection]);

  const columnsToExport = [
    'id_cabezal',
    'fecDoc',
    'fecVenc',
    'nombreDocumento',
    'nroDoca',
    'armador',
    'contenedor',
    'antiguedad',
    'viajeBuque',
    'seleccion',
    'referenciaLiquidacion',
    'comentarios',
    'razonSocial',
    'impMovMo',
  ];

  const onEditButtonClick = () => {
    if (!selectedItems?.length) return;
    if (isLoading || editAccountStatementModal) return;

    setEditAccountStatementModal(true);
  };

  const onChangeStateModal = () => {
    setEditAccountStatementModal(!editAccountStatementModal);
    setSelectedItems([]);
  };

  const isLoadingSelection = gettingSelection || creatingSelection || isLoading;

  return (
    <>
      {paymentData && isPaymentDialogShown && !isLoadingSelection && (
        <ModalPendingPayments
          oid={paymentData?.oid}
          isDialogShown={isPaymentDialogShown}
          toggleDialog={toggleIsPaymentDialog}
          formClientData={paymentData}
          getData={getData}
          accountStatementBody={{ retencionTitular, vencimiento, codigo }}
        />
      )}
      <Container className="w-full flex-1 overflow-auto">
        <header className="w-full flex flex-column gap-3">
          <h1 className="text-primary">{t('title')}</h1>

          <div className="w-full flex lg:flex-row flex-column lg:justify-content-between justify-center lg:align-items-end items-center">
            <AccountStatementFilter
              selectionData={selectionItems || []}
              filters={filters}
              setFilters={setFilters}
              setPaymentData={setPaymentData}
              codigoOptions={codigoOptions}
            />
            <div className="w-full flex align-items-center lg:align-items-end items-start gap-3 mt-2 md:mt-0 mb-1">
              <Button
                type="submit"
                className={`${styles.submitButton} flex w-auto lg:ml-4 h-12 my-0 px-5`}
                onClick={() => {}}
                disabled={codigo.length > 1}
              >
                <FontAwesomeIcon icon={faFilter} className={styles.icon} />
                {t('submitButtonText')}
              </Button>
              <div className="w-full">
                <ExportTableButton
                  data={accountStatement as unknown as TableData}
                  columnsToExport={columnsToExport}
                />
              </div>
            </div>
          </div>
        </header>
        <section className="w-full lg:mt-6 mt-2">
          {/* Header */}
          <div className="w-full flex flex-column md:flex-row justify-content-between md:align-items-center bg-secondary-light border-round-top p-3 gap-2">
            <div className="flex lg:flex-row flex-wrap divide-x divide-gray-400">
              <Button
                className="text-primary w-auto px-3 lg:mr-3 mr-1"
                type="button"
                green
                onClick={onPaymentButtonClick}
                disabled={
                  (!paymentData?.listaDocumentosSeleccionados?.length &&
                    !selectedItems?.length) ||
                  isLoadingSelection
                }
              >
                <span className="text-white flex gap-2 align-items-center">
                  <FontAwesomeIcon icon={faDollar} />
                  {paying ? t('paying') : t('pay')}
                </span>
              </Button>
              <Button
                className={`text-primary w-auto px-3 mb-0 bg-transparent ${styles.btnBorderLeft} ${styles.btnBorderRight}`}
                type="button"
                link
                disabled={!selectedItems?.length}
                onClick={onEditButtonClick}
              >
                <FontAwesomeIcon icon={faEdit} />
                {t('editComments')}
              </Button>
              <Button
                className="text-primary w-auto px-3 mb-0 bg-transparent"
                type="button"
                link
                onClick={() => setSelectionModalIsOpen(true)}
                disabled={!selectedItems?.length}
              >
                <FontAwesomeIcon icon={faClipboardCheck} />
                {t('createSelection')}
              </Button>

              {isTitular && (
                <Button
                  className={`text-primary w-auto px-3 mb-0 bg-transparent ${styles.btnBorderLeft}`}
                  type="button"
                  link
                  onClick={() => setWithholdinsModalIsOpen(true)}
                >
                  {t('seePendingWithholdings')}
                </Button>
              )}
            </div>
            <p className="text-primary">
              {!selectedItems?.length
                ? t('noSelectedItems')
                : t('selectesItems', { count: selectedItems.length })}
            </p>
          </div>
          <AccountStamentTable
            data={filterdData}
            isLoading={isLoading || isLoadingSelection}
            handleSelectionChange={handleSelectionChange}
            selectedItems={selectedItems}
            sumTotal={sumTotal}
            extraColumn={isTitular}
          />
          {/* Footer */}

          <div className="col-12 md:col-6  pl-0 pr-0 ">
            <CustomBox
              label={t('accountStatementMessage')}
              icon={<FontAwesomeIcon icon={faBoxesStacked} size="xl" />}
              iconColor="#223466"
              description=""
              backgroundColor="rgba(71, 128, 191, 0.1)"
            />
          </div>

          <EditAccountStamentModal
            visible={editAccountStatementModal}
            onClose={onChangeStateModal}
            paymentsOids={selectedItems}
            getData={() => getData({ retencionTitular, vencimiento, codigo })}
          />

          {selectionModalIsOpen && (
            <CreateSelectionModal
              selection={selectedItems}
              onClose={(success) => {
                setSelectionModalIsOpen(false);
                if (success) {
                  getData({ retencionTitular, vencimiento, codigo });
                }
              }}
              opened={selectionModalIsOpen}
              codigo={codigo}
              titular={titular || null}
              retencionTitular={retencionTitular}
              vencimiento={vencimiento as Date}
            />
          )}
        </section>

        {withholdinsModalIsOpen && (
          <PendingWithholdingsModal
            items={filterdData || []}
            onClose={() => setWithholdinsModalIsOpen(false)}
            opened={withholdinsModalIsOpen}
          />
        )}
      </Container>
    </>
  );
};

export default AccountStatement;
